import type { ApiOperation, ApiOperationOf, ApiOperationStatus } from './ApiSchemas/RecordTypes/ApiOperationSchema';
import type {
  ApiInitScanVideoUploadResponse,
  ApiScanVideoActiveWorkItem,
  ApiScanVideoProgress,
} from './ApiSchemas/ScanVideoUpload';
import { VIDEO_GROUP_IMAGE_CAPTURE_JOB_PHASES } from './ApiSchemas/ScanVideoUpload';
import type { KaiProApiClient } from './KaiProApiClient';

// *************************************************************
// Upload policy (shared across Renovator web and kaiPro mobile)
// *************************************************************

export const SCAN_VIDEO_CONTENT_HASH_HEX_LENGTH = 64;
const SCAN_VIDEO_CONTENT_HASH_PATTERN = /^[a-f0-9]{64}$/;

export const isValidScanVideoContentHash = (value: string): boolean => SCAN_VIDEO_CONTENT_HASH_PATTERN.test(value);

/** SHA-256 hex digest of file bytes for per-scan dedupe on init. */
export const computeScanVideoContentHash = async (file: Blob): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(hashBuffer)].map(byte => byte.toString(16).padStart(2, '0')).join('');
};

export const shouldUploadAfterScanVideoInit = (
  init: ApiInitScanVideoUploadResponse,
): boolean => !init.skipUpload;

export const shouldCompleteAfterScanVideoInit = (
  init: ApiInitScanVideoUploadResponse,
): boolean =>
  init.action === 'new'
  || init.action === 'resumeUpload'
  || init.action === 'retryAnalysis';

export const SCAN_VIDEO_MAX_FILES = 1;
/**
 * Overall picker ceiling. Large (>=1GB) walkthroughs route to multipart (see below), so this
 * matches SCAN_VIDEO_MULTIPART_MAX_BYTES. Provisional 8 GB / 30 min pending product (plan §14 Q1).
 */
export const SCAN_VIDEO_MAX_BYTES = 8 * 1024 * 1024 * 1024;
/**
 * Duration ceiling for all walkthroughs. The Renovator client uploads everything via multipart,
 * whose complete has no server-side duration check, so this single 30-min cap governs. Provisional
 * pending product (plan §14 Q1).
 */
export const SCAN_VIDEO_MAX_SECONDS = 1800;
export const SCAN_VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm';

/** Presigned PUT only — init/complete are not retried. */
export const SCAN_VIDEO_S3_PUT_MAX_ATTEMPTS = 3;
export const SCAN_VIDEO_S3_PUT_RETRY_DELAY_MS = 500;

// *************************************************************
// S3 multipart upload contract (large >=1GB walkthroughs)
//
// Contract-level constants shared with the server (which validates up to
// SCAN_VIDEO_MULTIPART_MAX_BYTES so client and server share one ceiling). The
// client-side slicing, streaming hash, part helpers, and bounded concurrency
// limiter that drive the multipart endpoints follow further below; the platform
// PUT/re-sign orchestration that wires them to the network lives in each app
// (Renovator's useAIScanVideoUpload).
// *************************************************************

/** At or above this size the client routes to the multipart endpoints instead of single-part PUT. */
export const SCAN_VIDEO_MULTIPART_THRESHOLD_BYTES = 1 * 1024 * 1024 * 1024;
/** Provisional upper bound for multipart uploads (see plan §14 — confirm with product). */
export const SCAN_VIDEO_MULTIPART_MAX_BYTES = 8 * 1024 * 1024 * 1024;
/** Server-dictated part size; returned in init so a resuming client slices identically. */
export const SCAN_VIDEO_MULTIPART_PART_SIZE_BYTES = 32 * 1024 * 1024;
/** S3 hard limit: at most 10,000 parts per multipart upload. */
export const SCAN_VIDEO_MULTIPART_MAX_PARTS = 10_000;
/** Per-part upload retry budget (only the failed part re-uploads). */
export const SCAN_VIDEO_PART_MAX_ATTEMPTS = 5;
/** Bounded in-flight part PUTs across the whole batch. */
export const SCAN_VIDEO_PART_CONCURRENCY = 4;

/** True when a file should upload via the multipart endpoints rather than single-part PUT. */
export const shouldUseScanVideoMultipart = (sizeBytes: number): boolean => sizeBytes >= SCAN_VIDEO_MULTIPART_THRESHOLD_BYTES;

// *************************************************************
// Multipart client behavior: streaming hash, slicing, part math, concurrency.
// Pure and platform-agnostic (no DOM / network). The web/mobile hooks inject the
// actual presigned-PUT and re-sign calls around these.
// *************************************************************

/** How many 1-based parts a file of sizeBytes splits into at partSizeBytes (min 1). */
export const getScanVideoPartCount = (sizeBytes: number, partSizeBytes: number): number => Math.max(1, Math.ceil(sizeBytes / partSizeBytes));

/** The byte slice for a 1-based part number. Part N covers [(N-1)*size, N*size). */
export const sliceScanVideoPart = (file: Blob, partNumber: number, partSizeBytes: number): Blob => {
  const start = (partNumber - 1) * partSizeBytes;
  return file.slice(start, start + partSizeBytes);
};

/** Part numbers in 1..totalParts not already uploaded (the resume gap), ascending. */
export const getMissingScanVideoPartNumbers = (
  totalParts: number,
  alreadyUploadedParts: readonly number[],
): number[] => {
  const uploaded = new Set(alreadyUploadedParts);
  const missing: number[] = [];
  for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
    if (!uploaded.has(partNumber)) missing.push(partNumber);
  }
  return missing;
};

export type ScanVideoUploadedPart = {
  partNumber: number;
  eTag: string;
};

/**
 * Build the CompleteMultipartUpload parts list from collected ETags, sorted by part number.
 * Returns [] when any ETag is missing (e.g. the S3 CORS policy did not expose the ETag header),
 * signaling the caller to send an empty parts list so the server reconciles from S3 ListParts.
 */
export const partsToComplete = (
  parts: readonly ScanVideoUploadedPart[],
): ScanVideoUploadedPart[] => {
  if (parts.some(part => !part.eTag)) return [];
  return [...parts].sort((a, b) => a.partNumber - b.partNumber);
};

export type ScanVideoConcurrencyLimiter = {
  run: <T>(fn: () => Promise<T>) => Promise<T>;
};

/**
 * A shared in-flight cap for part PUTs. One limiter is threaded across every file in a batch so
 * the whole batch never exceeds SCAN_VIDEO_PART_CONCURRENCY simultaneous part uploads (plan §8).
 */
export const createScanVideoConcurrencyLimiter = (limit: number): ScanVideoConcurrencyLimiter => {
  const max = Math.max(1, Math.floor(limit));
  let active = 0;
  const queue: (() => void)[] = [];

  const pump = () => {
    while (active < max && queue.length) {
      active++;
      const task = queue.shift()!;
      task();
    }
  };

  return {
    run: <T>(fn: () => Promise<T>): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        queue.push(() => {
          fn().then(resolve, reject).finally(() => {
            active--;
            pump();
          });
        });
        pump();
      }),
  };
};

/** Upload % from bytes already landed vs. total, clamped so it never reads 100 before complete. */
export const getScanVideoMultipartUploadPct = (uploadedBytes: number, totalBytes: number): number => {
  if (totalBytes <= 0) return 0;
  return Math.min(99, Math.round((uploadedBytes / totalBytes) * 100));
};

// *************************************************************
// Streaming SHA-256 — dedupe / resume key for multipart uploads.
//
// Why we chose a hand-written SHA-256 (deliberate first-pass choice, not an oversight):
//
//   1. The platform has no streaming hash. Web Crypto's crypto.subtle.digest() is
//      one-shot: it needs the whole input as a single ArrayBuffer. Walkthroughs run up
//      to SCAN_VIDEO_MULTIPART_MAX_BYTES (8 GB), which can't be materialized as one
//      buffer (engines cap an ArrayBuffer near ~2 GB, and it would OOM the tab anyway),
//      so the native API cannot hash a large file whole.
//   2. No hashing dependency exists in the repo, and adding one (hash-wasm, etc.) is a
//      real supply-chain / bundle / review commitment we don't want to take on for a
//      first pass. This is ~100 lines of a fixed, standardized algorithm with zero deps,
//      pinned by NIST test vectors and a byte-for-byte parity test against
//      computeScanVideoContentHash below — trivial to audit, and trivial to delete when
//      we swap in the upgrade.
//   3. The digest must be deterministic and stable across sessions: it is the per-scan
//      dedupe key AND the resume key (re-picking the same file must reproduce it). A
//      spec-exact SHA-256 gives that, and matches the whole-file digest used by the
//      single-part server path, so the two agree on file identity for dedupe.
//
// It reads fixed-size slices and folds each into the running digest, so peak memory is
// one chunk (~32 MB) regardless of file size — the actual off-heap requirement.
// *************************************************************

const SHA256_K = new Uint32Array([
  0x428a2f98,
  0x71374491,
  0xb5c0fbcf,
  0xe9b5dba5,
  0x3956c25b,
  0x59f111f1,
  0x923f82a4,
  0xab1c5ed5,
  0xd807aa98,
  0x12835b01,
  0x243185be,
  0x550c7dc3,
  0x72be5d74,
  0x80deb1fe,
  0x9bdc06a7,
  0xc19bf174,
  0xe49b69c1,
  0xefbe4786,
  0x0fc19dc6,
  0x240ca1cc,
  0x2de92c6f,
  0x4a7484aa,
  0x5cb0a9dc,
  0x76f988da,
  0x983e5152,
  0xa831c66d,
  0xb00327c8,
  0xbf597fc7,
  0xc6e00bf3,
  0xd5a79147,
  0x06ca6351,
  0x14292967,
  0x27b70a85,
  0x2e1b2138,
  0x4d2c6dfc,
  0x53380d13,
  0x650a7354,
  0x766a0abb,
  0x81c2c92e,
  0x92722c85,
  0xa2bfe8a1,
  0xa81a664b,
  0xc24b8b70,
  0xc76c51a3,
  0xd192e819,
  0xd6990624,
  0xf40e3585,
  0x106aa070,
  0x19a4c116,
  0x1e376c08,
  0x2748774c,
  0x34b0bcb5,
  0x391c0cb3,
  0x4ed8aa4a,
  0x5b9cca4f,
  0x682e6ff3,
  0x748f82ee,
  0x78a5636f,
  0x84c87814,
  0x8cc70208,
  0x90befffa,
  0xa4506ceb,
  0xbef9a3f7,
  0xc67178f2,
]);

class ScanVideoSha256 {
  private readonly h = new Int32Array([
    0x6a09e667,
    0xbb67ae85,
    0x3c6ef372,
    0xa54ff53a,
    0x510e527f,
    0x9b05688c,
    0x1f83d9ab,
    0x5be0cd19,
  ]);
  private readonly block = new Uint8Array(64);
  private readonly w = new Int32Array(64);
  private blockLength = 0;
  private bytesHashed = 0;

  private process(data: Uint8Array, offset: number) {
    const { w, h } = this;
    for (let i = 0; i < 16; i++) {
      const j = offset + i * 4;
      w[i] = (data[j] << 24) | (data[j + 1] << 16) | (data[j + 2] << 8) | data[j + 3];
    }
    for (let i = 16; i < 64; i++) {
      const x = w[i - 15];
      const y = w[i - 2];
      const s0 = ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3);
      const s1 = ((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let a = h[0], b = h[1], c = h[2], d = h[3], e = h[4], f = h[5], g = h[6], hh = h[7];
    for (let i = 0; i < 64; i++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + (SHA256_K[i] | 0) + w[i]) | 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    h[0] = (h[0] + a) | 0;
    h[1] = (h[1] + b) | 0;
    h[2] = (h[2] + c) | 0;
    h[3] = (h[3] + d) | 0;
    h[4] = (h[4] + e) | 0;
    h[5] = (h[5] + f) | 0;
    h[6] = (h[6] + g) | 0;
    h[7] = (h[7] + hh) | 0;
  }

  update(data: Uint8Array) {
    this.bytesHashed += data.length;
    let offset = 0;

    if (this.blockLength > 0) {
      const take = Math.min(64 - this.blockLength, data.length);
      this.block.set(data.subarray(0, take), this.blockLength);
      this.blockLength += take;
      offset = take;
      if (this.blockLength === 64) {
        this.process(this.block, 0);
        this.blockLength = 0;
      }
    }

    while (offset + 64 <= data.length) {
      this.process(data, offset);
      offset += 64;
    }

    if (offset < data.length) {
      this.block.set(data.subarray(offset), 0);
      this.blockLength = data.length - offset;
    }
  }

  digestHex(): string {
    const bitLength = this.bytesHashed * 8;
    const pad = new Uint8Array((this.blockLength < 56 ? 56 : 120) - this.blockLength + 8);
    pad[0] = 0x80;
    // 64-bit big-endian bit length in the final 8 bytes (exact for sizes up to 2^53).
    const hi = Math.floor(bitLength / 0x100000000);
    const lo = bitLength >>> 0;
    const tail = pad.length - 8;
    pad[tail] = (hi >>> 24) & 0xff;
    pad[tail + 1] = (hi >>> 16) & 0xff;
    pad[tail + 2] = (hi >>> 8) & 0xff;
    pad[tail + 3] = hi & 0xff;
    pad[tail + 4] = (lo >>> 24) & 0xff;
    pad[tail + 5] = (lo >>> 16) & 0xff;
    pad[tail + 6] = (lo >>> 8) & 0xff;
    pad[tail + 7] = lo & 0xff;
    this.update(pad);

    let hex = '';
    for (let i = 0; i < 8; i++) {
      hex += (this.h[i] >>> 0).toString(16).padStart(8, '0');
    }
    return hex;
  }
}

/**
 * SHA-256 hex digest of a (potentially multi-GB) file, read in slices so the whole file
 * never enters the JS heap at once. Use on the multipart path; identical output to
 * computeScanVideoContentHash for the same bytes.
 */
export const computeScanVideoContentHashStreaming = async (
  file: Blob,
  chunkSizeBytes: number = SCAN_VIDEO_MULTIPART_PART_SIZE_BYTES,
): Promise<string> => {
  const hasher = new ScanVideoSha256();
  const chunkSize = Math.max(1, chunkSizeBytes);
  for (let start = 0; start < file.size; start += chunkSize) {
    const slice = file.slice(start, Math.min(start + chunkSize, file.size));
    hasher.update(new Uint8Array(await slice.arrayBuffer()));
  }
  return hasher.digestHex();
};

// Per-video analyze poll cadence. There is no wall-clock give-up: the server is the source of truth
// for terminal state (JobRunner marks completed/failed on success/throw, and the external-service
// waits carry their own timeouts), so a healthy long job is polled until it reports terminal rather
// than being failed on elapsed time.
export const SCAN_VIDEO_JOB_POLL_INTERVAL_MS = 2500;

// The one client-side exit that is NOT wall-clock: give up after this many *consecutive* failed reads
// for a video (a thrown getOperation, a missing operation id, or a non-analysis record). The counter
// resets to zero on any successful read, so a healthy long job never trips it — only a genuinely
// unreachable operation does (pruned/404, persistent 5xx, 403 after an org switch). Without this the
// error path never removes the video from the poll set and the interval runs for the life of the tab.
export const SCAN_VIDEO_JOB_POLL_MAX_CONSECUTIVE_FAILURES = 5;
export const SCAN_VIDEO_JOB_POLL_CONNECTION_LOST_MESSAGE = 'Lost contact with the analysis service. It may still be running — reopen to check, or try again.';

/** Poll interval for trigger-button / active-work discovery (lighter than per-video job poll). */
export const SCAN_VIDEO_ACTIVE_WORK_POLL_INTERVAL_MS = 5000;

/** Synthetic terminal progress used when the poll gives up after repeated unreachable reads (not a wall-clock timeout). */
export const buildScanVideoPollConnectionLostProgress = (
  videoId: string,
): ApiScanVideoProgress => ({
  videoId,
  lifecycleStatus: 'failed',
  progress: null,
  error: SCAN_VIDEO_JOB_POLL_CONNECTION_LOST_MESSAGE,
});

// *************************************************************
// File descriptor + validation
// *************************************************************

const VIDEO_MIME_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/webm',
]);

const EXTENSION_TO_VIDEO_MIME: Record<string, string> = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
};

/** Minimal file metadata shared by web File and mobile picker results. */
export type ScanVideoFileLike = {
  name: string;
  size: number;
  type: string;
};

export const getVideoFileExtension = (fileName: string): string => String(fileName || '').split('.').pop()?.toLowerCase() ?? '';

/** Allowed MIME from file.type, or from extension when the platform omits type. */
export const resolveVideoContentType = (file: ScanVideoFileLike): string | null => {
  if (VIDEO_MIME_TYPES.has(file.type)) return file.type;
  return EXTENSION_TO_VIDEO_MIME[getVideoFileExtension(file.name)] ?? null;
};

export const isAcceptedVideoFile = (file: ScanVideoFileLike): boolean => resolveVideoContentType(file) !== null;

export const formatVideoFileSize = (bytes: number): string => {
  const mb = bytes / 1_048_576;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  if (mb < 1) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return mb < 10 ? `${mb.toFixed(1)} MB` : `${Math.round(mb)} MB`;
};

export const formatVideoDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${minutes}:${String(secs).padStart(2, '0')}`;
};

/** Sync validation only — type and size. Duration is checked separately. */
export const getSyncVideoFileValidationError = (file: ScanVideoFileLike): string | null => {
  if (!isAcceptedVideoFile(file)) return 'Unsupported format';
  if (file.size > SCAN_VIDEO_MAX_BYTES) {
    return `${formatVideoFileSize(file.size)} · over the 8 GB limit`;
  }
  return null;
};

export const getDurationValidationError = (
  durationSeconds: number | null,
): string | null => {
  if (durationSeconds === null) return null;
  if (durationSeconds > SCAN_VIDEO_MAX_SECONDS) {
    return `${formatVideoDuration(durationSeconds)} · over the 30 min limit`;
  }
  return null;
};

// *************************************************************
// Multi-file picker row model
// *************************************************************

export type SelectedVideoFileState =
  | 'checking'
  | 'validated'
  | 'bad'
  | 'uploading'
  | 'uploaded'
  | 'analyzing'
  | 'analyzed'
  | 'analysisFailed';

export type SelectedVideoFile<TFile extends ScanVideoFileLike = ScanVideoFileLike> = {
  id: string;
  file: TFile;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  videoRecordedAt: Date | null;
  uploadPct: number;
  state: SelectedVideoFileState;
  videoId?: string;
  jobStepIndex?: number;
  stepFlash?: boolean;
  reason?: string;
};

export const hasScanVideoActiveWork = (
  items: readonly ApiScanVideoActiveWorkItem[],
): boolean => items.length > 0;

/** Idle label for upload trigger buttons; switches to analyzing copy while jobs are active. */
export const getScanVideoUploadTriggerLabel = (
  idleLabel: string,
  activeCount: number,
): string => {
  if (activeCount <= 0) return idleLabel;
  if (activeCount === 1) return 'Analyzing video…';
  return `Analyzing videos (${activeCount})`;
};

/** Label for the Focus Panel "+ New Video" chip while analyze jobs are running. */
export const getScanVideoNewVideoTriggerLabel = (activeCount: number): string => activeCount > 0 ? 'Analyzing…' : '+ New Video';

export const buildSelectedVideoFileFromActiveWorkItem = (
  item: ApiScanVideoActiveWorkItem,
): SelectedVideoFile => ({
  id: item.videoId,
  file: {
    name: item.originalFileName,
    size: item.sizeBytes,
    type: item.contentType,
  },
  durationSeconds: null,
  width: null,
  height: null,
  videoRecordedAt: null,
  uploadPct: 100,
  state: 'analyzing',
  videoId: item.videoId,
  jobStepIndex: item.progress?.stepIndex,
});

export const getRowProgressBarPct = (
  row: SelectedVideoFile,
  jobProgress?: ApiScanVideoProgress | null,
): number => {
  if (row.state === 'uploading') return row.uploadPct;
  if (row.state === 'uploaded') return 100;
  if (row.state === 'analyzing') {
    const stepIndex = jobProgress?.progress?.stepIndex ?? row.jobStepIndex ?? 0;
    const totalSteps = jobProgress?.progress?.totalSteps ?? VIDEO_GROUP_IMAGE_CAPTURE_JOB_PHASES.length;
    if (stepIndex > 0 && totalSteps > 0) {
      return Math.min(99, Math.round((stepIndex / totalSteps) * 100));
    }
    return 4;
  }
  if (row.state === 'analyzed') return 100;
  return 0;
};

export const shouldShowRowProgressBar = (row: SelectedVideoFile): boolean =>
  row.state === 'uploading' || row.state === 'uploaded' || row.state === 'analyzing' || row.state === 'analyzed';

export const getSelectedVideoFileSubtitle = (
  row: SelectedVideoFile,
  jobProgress?: ApiScanVideoProgress | null,
): string => {
  const sizeLabel = row.file.size ? formatVideoFileSize(row.file.size) : '';
  const durationLabel = formatVideoDuration(row.durationSeconds ?? NaN);

  if (row.state === 'bad') return row.reason ?? 'Unsupported file';
  if (row.state === 'checking') return 'Checking…';
  if (row.state === 'uploading') return `Uploading… ${Math.round(row.uploadPct)}%`;
  if (row.state === 'uploaded') return 'Uploaded';
  if (row.state === 'analyzing') {
    if (jobProgress?.lifecycleStatus === 'created' || !jobProgress?.progress?.message) {
      return 'Queued';
    }
    return jobProgress.progress.message;
  }
  if (row.state === 'analyzed') return 'Analyzed';
  if (row.state === 'analysisFailed') {
    return row.reason ?? jobProgress?.error ?? 'Analysis failed';
  }

  return [sizeLabel, durationLabel].filter(Boolean).join(' · ');
};

export const isRowRemoveDisabled = (row: SelectedVideoFile): boolean =>
  row.state === 'uploading'
  || row.state === 'uploaded'
  || row.state === 'analyzing'
  || row.state === 'analyzed'
  || row.state === 'analysisFailed';

export type PickerFooterHint = {
  text: string;
  isError: boolean;
};

export const getPickerFooterHint = (
  rows: readonly SelectedVideoFile[],
  overflowNotice?: string,
): PickerFooterHint => {
  if (overflowNotice) return { text: overflowNotice, isError: true };
  if (!rows.length) return { text: '', isError: false };

  const validatedCount = rows.filter(row => row.state === 'validated').length;
  const badCount = rows.filter(row => row.state === 'bad').length;
  const checkingCount = rows.filter(row => row.state === 'checking').length;
  const uploadingCount = rows.filter(row => row.state === 'uploading').length;
  const uploadedCount = rows.filter(row => row.state === 'uploaded').length;
  const analyzingCount = rows.filter(row => row.state === 'analyzing').length;
  const analyzedCount = rows.filter(row => row.state === 'analyzed').length;
  const failedCount = rows.filter(row => row.state === 'analysisFailed').length;

  if (checkingCount > 0) {
    return { text: `Checking ${checkingCount} of ${rows.length}`, isError: false };
  }
  if (uploadingCount > 0) {
    return {
      text: analyzingCount > 0 || analyzedCount > 0
        ? `Uploading ${uploadingCount} · ${analyzedCount + analyzingCount} analyzing`
        : `Uploading ${uploadingCount} of ${rows.length}`,
      isError: false,
    };
  }
  if (uploadedCount > 0 && analyzingCount === 0 && analyzedCount === 0 && failedCount === 0) {
    return { text: 'Uploaded · starting analysis', isError: false };
  }
  if (analyzingCount > 0) {
    return {
      text: `${analyzingCount} analyzing${analyzedCount > 0 ? ` · ${analyzedCount} done` : ''}`,
      isError: false,
    };
  }
  if (allBatchRowsTerminal(rows)) {
    if (failedCount > 0) {
      return {
        text: `${analyzedCount} analyzed · ${failedCount} failed`,
        isError: true,
      };
    }
    return { text: `${analyzedCount} analyzed · ready to close`, isError: false };
  }
  if (badCount > 0 && validatedCount > 0) {
    return {
      text: `${validatedCount} ready · ${badCount} can\u2019t be used`,
      isError: true,
    };
  }
  if (badCount > 0) {
    return { text: `${badCount} can\u2019t be used`, isError: true };
  }
  if (validatedCount > 0) {
    return {
      text: `${validatedCount} of ${SCAN_VIDEO_MAX_FILES} ready to upload`,
      isError: false,
    };
  }
  return { text: '', isError: false };
};

export const hasPendingValidation = (rows: readonly SelectedVideoFile[]): boolean => rows.some(row => row.state === 'checking');

export const hasPendingUploads = (rows: readonly SelectedVideoFile[]): boolean => rows.some(row => row.state === 'uploading');

export const hasActiveAnalysis = (rows: readonly SelectedVideoFile[]): boolean => rows.some(row => row.state === 'analyzing');

export const isBatchActive = (rows: readonly SelectedVideoFile[]): boolean =>
  rows.some(row =>
    row.state === 'uploading'
    || row.state === 'uploaded'
    || row.state === 'analyzing'
    || row.state === 'analyzed'
    || row.state === 'analysisFailed'
  );

export const canSubmitBatch = (rows: readonly SelectedVideoFile[]): boolean => {
  const validatedCount = rows.filter(row => row.state === 'validated').length;
  if (validatedCount === 0) return false;
  return !rows.some(row =>
    row.state === 'checking'
    || row.state === 'uploading'
    || row.state === 'uploaded'
    || row.state === 'analyzing'
    || row.state === 'analyzed'
    || row.state === 'analysisFailed'
  );
};

export const allBatchRowsTerminal = (rows: readonly SelectedVideoFile[]): boolean => {
  const batchRows = rows.filter(row =>
    row.state === 'uploading'
    || row.state === 'uploaded'
    || row.state === 'analyzing'
    || row.state === 'analyzed'
    || row.state === 'analysisFailed'
  );
  if (!batchRows.length) return false;
  return batchRows.every(row => row.state === 'analyzed' || row.state === 'analysisFailed');
};

export const getValidatedFiles = <TFile extends ScanVideoFileLike>(
  rows: readonly SelectedVideoFile<TFile>[],
): SelectedVideoFile<TFile>[] => rows.filter((row): row is SelectedVideoFile<TFile> => row.state === 'validated');

export const createSelectedVideoFile = <TFile extends ScanVideoFileLike>(
  file: TFile,
  id: string,
): SelectedVideoFile<TFile> => {
  const syncError = getSyncVideoFileValidationError(file);
  if (syncError) {
    return {
      id,
      file,
      durationSeconds: null,
      width: null,
      height: null,
      videoRecordedAt: null,
      uploadPct: 0,
      state: 'bad',
      reason: syncError,
    };
  }
  return {
    id,
    file,
    durationSeconds: null,
    width: null,
    height: null,
    videoRecordedAt: null,
    uploadPct: 0,
    state: 'checking',
  };
};

export const acceptVideoFiles = <TFile extends ScanVideoFileLike>(
  existing: readonly SelectedVideoFile<TFile>[],
  incoming: readonly TFile[],
  nextId: () => string,
): {
  rows: SelectedVideoFile<TFile>[];
  accepted: SelectedVideoFile<TFile>[];
  overflowCount: number;
} => {
  const room = SCAN_VIDEO_MAX_FILES - existing.length;
  const take = incoming.slice(0, Math.max(0, room));
  const overflowCount = Math.max(0, incoming.length - take.length);
  const accepted = take.map(file => createSelectedVideoFile(file, nextId()));
  return {
    rows: [...existing, ...accepted],
    accepted,
    overflowCount,
  };
};

export const getOverflowNotice = (overflowCount: number): string | undefined =>
  overflowCount > 0 ? `1 video per upload · ${overflowCount} not added` : undefined;

// *************************************************************
// VideoGroupImageCaptureJob — Operations API client helpers
// *************************************************************

/** The Operation record narrowed to walkthrough analyze (`VideoGroupImageCaptureJob`). */
export type VideoGroupImageCaptureOperation = ApiOperationOf<'VideoGroupImageCaptureJob'>;

export const isVideoGroupImageCaptureOperation = (
  operation: ApiOperation,
): operation is VideoGroupImageCaptureOperation => operation.operationType === 'VideoGroupImageCaptureJob';

/** The Operation record narrowed to the scope-building analyze (`VideoToScopeJob`). */
export type VideoToScopeOperation = ApiOperationOf<'VideoToScopeJob'>;

export const isVideoToScopeOperation = (
  operation: ApiOperation,
): operation is VideoToScopeOperation => operation.operationType === 'VideoToScopeJob';

/** Either walkthrough-analysis operation the upload modal can start and poll. */
export type ScanVideoAnalysisOperation = VideoToScopeOperation | VideoGroupImageCaptureOperation;

export const isScanVideoAnalysisOperation = (
  operation: ApiOperation,
): operation is ScanVideoAnalysisOperation => isVideoToScopeOperation(operation) || isVideoGroupImageCaptureOperation(operation);

export const readVideoIdFromVideoGroupImageCaptureInput = (
  input: VideoGroupImageCaptureOperation['input'],
): string | undefined => {
  const videoId = (input as { videoId?: unknown }).videoId;
  return typeof videoId === 'string' ? videoId : undefined;
};

export const readScanIdFromVideoGroupImageCaptureInput = (
  input: VideoGroupImageCaptureOperation['input'],
): string | undefined => {
  const scanId = (input as { scanId?: unknown }).scanId;
  return typeof scanId === 'string' ? scanId : undefined;
};

const isActiveOperationStatus = (status: ApiOperationStatus) => status === 'created' || status === 'started';

/** Maps a typed Operation record into the legacy `ApiScanVideoProgress` shape the upload UI consumes. */
export const toApiScanVideoProgressFromOperation = (
  videoId: string,
  operation: Pick<ScanVideoAnalysisOperation, 'status' | 'progressDetails' | 'error'>,
): ApiScanVideoProgress => ({
  videoId,
  lifecycleStatus: operation.status,
  progress: operation.progressDetails
    ? {
      phase: operation.progressDetails.phase,
      message: operation.progressDetails.message,
      stepIndex: operation.progressDetails.stepIndex,
      totalSteps: operation.progressDetails.totalSteps,
      updatedAt: operation.progressDetails.updatedAt,
    }
    : null,
  error: operation.status === 'failed' ? operation.error?.message : undefined,
});

export const kickOffVideoGroupImageCaptureJob = async (
  kaiProApiClient: KaiProApiClient,
  videoId: string,
): Promise<VideoGroupImageCaptureOperation> => {
  const operation = await kaiProApiClient.createOperation({
    operationType: 'VideoGroupImageCaptureJob',
    input: { videoId },
  });
  if (!isVideoGroupImageCaptureOperation(operation)) {
    throw new Error('Expected VideoGroupImageCaptureJob operation');
  }
  return operation;
};

export const listActiveVideoGroupImageCaptureOperationsForScan = (
  kaiProApiClient: KaiProApiClient,
  scanId: string,
): Promise<VideoGroupImageCaptureOperation[]> =>
  kaiProApiClient
    .listOperations({
      operationType: 'VideoGroupImageCaptureJob',
      status: ['created', 'started'],
      limit: 100,
    })
    .then(operations =>
      operations
        .filter(isVideoGroupImageCaptureOperation)
        .filter(operation => readScanIdFromVideoGroupImageCaptureInput(operation.input) === scanId)
    );

/** LIST is ordered updatedAt desc — keep the newest active operation per video. */
export const pickLatestVideoGroupImageCaptureOperationPerVideo = (
  operations: VideoGroupImageCaptureOperation[],
): VideoGroupImageCaptureOperation[] => {
  const latestByVideoId = new Map<string, VideoGroupImageCaptureOperation>();
  for (const operation of operations) {
    const videoId = readVideoIdFromVideoGroupImageCaptureInput(operation.input);
    if (!videoId || latestByVideoId.has(videoId)) continue;
    latestByVideoId.set(videoId, operation);
  }
  return [...latestByVideoId.values()];
};

export const toApiScanVideoActiveWorkItemFromOperation = (
  submission: { id: string; originalFileName: string; mime: string; bytes: number },
  operation: ScanVideoAnalysisOperation,
): ApiScanVideoActiveWorkItem => {
  const progress = toApiScanVideoProgressFromOperation(submission.id, operation);
  return {
    videoId: submission.id,
    originalFileName: submission.originalFileName,
    contentType: submission.mime,
    sizeBytes: submission.bytes,
    phase: 'analyzing',
    lifecycleStatus: progress.lifecycleStatus ?? undefined,
    progress: progress.progress ?? undefined,
  };
};

export const resolveVideoGroupImageCaptureProgressMessage = (
  operation: Pick<VideoGroupImageCaptureOperation, 'status' | 'progressDetails'>,
): string | undefined => {
  if (operation.status === 'failed') return 'Analysis failed';
  if (operation.status === 'completed') return 'Analysis complete';
  if (operation.progressDetails?.message) return operation.progressDetails.message;
  const phase = operation.progressDetails?.phase;
  if (!phase) return operation.status === 'created' ? 'Queued for analysis' : 'Analyzing…';
  return VIDEO_GROUP_IMAGE_CAPTURE_JOB_PHASES.find(entry => entry.phase === phase)?.message ?? 'Analyzing…';
};

export const isVideoGroupImageCaptureOperationInProgress = (
  status: ApiOperationStatus | null | undefined,
): boolean => status != null && isActiveOperationStatus(status);

// *************************************************************
// VideoToScopeJob + shared analysis Operations API client helpers
// *************************************************************

// Both analysis operations share the `{ videoId, scanId }` input shape, so one reader serves either.
export const readVideoIdFromScanVideoAnalysisInput = readVideoIdFromVideoGroupImageCaptureInput;
export const readScanIdFromScanVideoAnalysisInput = readScanIdFromVideoGroupImageCaptureInput;

/**
 * Starts `VideoToScopeJob` for an uploaded video, or gets back the one already started for it.
 * `scanGroupId` is the Scan Group (room) the video was recorded for, when known.
 */
export const kickOffVideoToScopeJob = async (
  kaiProApiClient: Pick<KaiProApiClient, 'createOperation'>,
  videoId: string,
  scanGroupId?: string,
): Promise<VideoToScopeOperation> => {
  const operation = await kaiProApiClient.createOperation({
    operationType: 'VideoToScopeJob',
    input: { videoId, scanGroupId },
  });
  if (!isVideoToScopeOperation(operation)) {
    throw new Error('Expected VideoToScopeJob operation');
  }
  return operation;
};

export const listActiveVideoToScopeOperationsForScan = (
  kaiProApiClient: KaiProApiClient,
  scanId: string,
): Promise<VideoToScopeOperation[]> =>
  kaiProApiClient
    .listOperations({
      operationType: 'VideoToScopeJob',
      status: ['created', 'started'],
      limit: 100,
    })
    .then(operations =>
      operations
        .filter(isVideoToScopeOperation)
        .filter(operation => readScanIdFromScanVideoAnalysisInput(operation.input) === scanId)
    );

/** Active operations of both analysis types for a scan (VideoToScope + VideoGroupImageCapture). */
export const listActiveScanVideoAnalysisOperationsForScan = async (
  kaiProApiClient: KaiProApiClient,
  scanId: string,
): Promise<ScanVideoAnalysisOperation[]> => {
  const [videoToScope, videoGroupImageCapture] = await Promise.all([
    listActiveVideoToScopeOperationsForScan(kaiProApiClient, scanId),
    listActiveVideoGroupImageCaptureOperationsForScan(kaiProApiClient, scanId),
  ]);
  return [...videoToScope, ...videoGroupImageCapture];
};

/** LIST is ordered updatedAt desc — keep the newest active operation per video, across both types. */
export const pickLatestScanVideoAnalysisOperationPerVideo = (
  operations: ScanVideoAnalysisOperation[],
): ScanVideoAnalysisOperation[] => {
  const latestByVideoId = new Map<string, ScanVideoAnalysisOperation>();
  for (const operation of operations) {
    const videoId = readVideoIdFromScanVideoAnalysisInput(operation.input);
    if (!videoId || latestByVideoId.has(videoId)) continue;
    latestByVideoId.set(videoId, operation);
  }
  return [...latestByVideoId.values()];
};

// *************************************************************
// Interaction correlation — owned analysis-operation ids per scan
//
// The active-work discovery LIST is scan + user + status scoped, so it also
// returns a user's OTHER in-flight analysis operations for the same scan (e.g. a
// job stuck non-terminal from a prior session, or one started in another tab).
// Rehydration must adopt only operations THIS interaction actually started, so we
// remember started operation ids per scan in sessionStorage. That survives modal
// close/reopen and same-tab reload, and is intentionally dropped on a new/closed
// tab — where the safe default is to adopt nothing rather than a stranger job.
//
// Pure over an injected storage so it is unit-testable without a DOM; callers on
// web/mobile pass their platform storage (or rely on the sessionStorage default).
// *************************************************************

export type ScanVideoOperationIdStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

const SCAN_VIDEO_OPERATION_IDS_STORAGE_PREFIX = 'kai.scanVideo.operationIds.';

const scanVideoOperationIdsStorageKey = (scanId: string): string => `${SCAN_VIDEO_OPERATION_IDS_STORAGE_PREFIX}${scanId}`;

/** The ambient sessionStorage, or undefined when it is absent/blocked (SSR, private mode, tests). */
const resolveScanVideoOperationIdStorage = (
  storage?: ScanVideoOperationIdStorage,
): ScanVideoOperationIdStorage | undefined => {
  if (storage) return storage;
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
};

const readScanVideoOperationIdArray = (
  storage: ScanVideoOperationIdStorage,
  key: string,
): string[] => {
  try {
    const raw = storage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

/** Operation ids this tab started for the scan; empty when nothing is remembered or storage is unavailable. */
export const readRememberedScanVideoOperationIds = (
  scanId: string,
  storage?: ScanVideoOperationIdStorage,
): Set<string> => {
  const store = resolveScanVideoOperationIdStorage(storage);
  if (!store || !scanId) return new Set();
  return new Set(readScanVideoOperationIdArray(store, scanVideoOperationIdsStorageKey(scanId)));
};

/** Record an operation id as started by this interaction; a no-op when storage is unavailable/full. */
export const rememberScanVideoOperationId = (
  scanId: string,
  operationId: string,
  storage?: ScanVideoOperationIdStorage,
): void => {
  const store = resolveScanVideoOperationIdStorage(storage);
  if (!store || !scanId || !operationId) return;
  const key = scanVideoOperationIdsStorageKey(scanId);
  const existing = readScanVideoOperationIdArray(store, key);
  if (existing.includes(operationId)) return;
  try {
    store.setItem(key, JSON.stringify([...existing, operationId]));
  } catch {
    // Storage full/blocked — degrade to not remembering (later discovery adopts nothing, which is safe).
  }
};

/** Drop a remembered operation id once it reaches a terminal status so the set never grows unbounded. */
export const forgetScanVideoOperationId = (
  scanId: string,
  operationId: string,
  storage?: ScanVideoOperationIdStorage,
): void => {
  const store = resolveScanVideoOperationIdStorage(storage);
  if (!store || !scanId || !operationId) return;
  const key = scanVideoOperationIdsStorageKey(scanId);
  const existing = readScanVideoOperationIdArray(store, key);
  if (!existing.includes(operationId)) return;
  const next = existing.filter(id => id !== operationId);
  try {
    if (next.length) store.setItem(key, JSON.stringify(next));
    else store.removeItem(key);
  } catch {
    // Best-effort cleanup; a stale id simply ages out with the tab.
  }
};
